import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Settings,
  LogOut, Menu, X, ChevronRight, Tag, Globe, Bell, Search,
  Plus, User, BarChart2,
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const NAV = [
  { label: 'Dashboard',  to: '/admin',          icon: LayoutDashboard, end: true },
  { label: 'Orders',     to: '/admin/orders',    icon: ShoppingCart },
  { label: 'Products',   to: '/admin/products',  icon: Package },
  { label: 'Customers',  to: '/admin/customers', icon: Users },
  { label: 'Analytics',  to: '/admin/analytics', icon: BarChart2 },
  { label: 'Discounts',  to: '/admin/discounts',  icon: Tag },
  { label: 'Settings',   to: '/admin/settings',  icon: Settings },
];

function SidebarLink({ to, icon: Icon, label, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-[0.72rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors rounded-[2px] group ${
          isActive
            ? 'bg-[var(--brand)] text-[#09090b]'
            : 'text-[var(--muted)] hover:text-[var(--ink)] hover:bg-white/5'
        }`
      }
    >
      <Icon size={14} strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const SidebarContent = ({ onLinkClick }) => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-4 py-5 border-b border-[var(--line)] shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 bg-[var(--brand)] flex items-center justify-center">
            <span className="font-['Space_Mono'] text-[0.55rem] font-bold text-[#09090b]">SP</span>
          </div>
          <div>
            <p className="font-['Space_Mono'] text-[0.62rem] tracking-[0.18em] uppercase text-[var(--ink)]">
              Smple Product
            </p>
            <p className="font-['Space_Mono'] text-[0.5rem] tracking-[0.12em] uppercase text-[var(--muted)]">
              Admin
            </p>
          </div>
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5 overflow-y-auto">
        {NAV.map((item) => (
          <SidebarLink
            key={item.to}
            {...item}
            onClick={() => onLinkClick?.()}
          />
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-[var(--line)] shrink-0">
        <div className="flex items-center gap-2 px-3 py-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-[var(--brand)] flex items-center justify-center">
            <User size={11} className="text-[#09090b]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[0.65rem] font-['Space_Mono'] text-[var(--ink)] truncate">
              {admin?.email || 'Admin'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 text-[0.65rem] font-['Space_Mono'] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-red-400 transition-colors"
        >
          <LogOut size={13} strokeWidth={1.5} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* ── Desktop sidebar ──────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-52 shrink-0 bg-[var(--paper)] border-r border-[var(--line)] fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {/* ── Mobile sidebar overlay ───────────────────────── */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="relative z-10 w-52 bg-[var(--paper)] border-r border-[var(--line)] flex flex-col">
            <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* ── Main area ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col md:ml-52 min-w-0">
        {/* Topbar */}
        <header className="h-12 bg-[var(--paper)] border-b border-[var(--line)] flex items-center px-4 gap-4 sticky top-0 z-30">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
          >
            <Menu size={16} />
          </button>

          {/* Search */}
          <div className="flex-1 max-w-xs relative hidden sm:block">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full bg-[var(--bg)] border border-[var(--line)] text-[var(--ink)] text-xs font-['Space_Grotesk'] pl-8 pr-3 py-1.5 focus:outline-none focus:border-[var(--brand)] transition-colors"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Quick add product */}
            <button
              onClick={() => navigate('/admin/products/new')}
              className="hidden sm:flex items-center gap-1.5 btn-brand px-3 py-1.5 text-[0.6rem]"
            >
              <Plus size={11} />
              Add Product
            </button>

            {/* Visit store */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[0.6rem] font-['Space_Mono'] tracking-[0.1em] uppercase text-[var(--muted)] hover:text-[var(--ink)] transition-colors px-2 py-1.5"
            >
              <Globe size={11} />
              <span className="hidden sm:inline">Store</span>
            </a>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
